import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CategoryTag } from '../../components/CategoryTag';
import { ApiError, api, type CategorySuggestions } from '../../lib/api';
import { absMoney } from '../../lib/format';

/**
 * A IA sugerindo categoria sob demanda.
 *
 * Desde que a IA passou a classificar sozinha ao fim de cada varredura, esta
 * tela deixou de ser o unico caminho e virou o caminho do RESTO: o que ela
 * declinou da primeira vez, e o que o "Outros" acumulou antes de ela existir.
 * Continua valendo a pena porque aqui a pessoa ve linha por linha antes de
 * gravar, e porque um lote grande de historico nao deve entrar sem olhar.
 *
 * Duas coisas que a tela precisa deixar claras, e deixa:
 *
 * Aqui, nada e aplicado sozinho. Cada linha vem marcada, com o motivo escrito ao
 * lado, e so vira dado quando a pessoa clica. Uma categorizacao errada nao
 * avisa que esta errada: ela some no meio de trezentos lancamentos e
 * reaparece meses depois num relatorio que nao bate.
 *
 * Quando a IA nao esta disponivel — servidor sem a assinatura configurada,
 * limite de uso atingido, demora demais — a tela diz o motivo em vez de
 * ficar girando. O resto do sistema nao depende dela para nada.
 */
export function SugestoesDeCategoria() {
  const cliente = useQueryClient();
  const [resultado, setResultado] = useState<CategorySuggestions | null>(null);
  const [recusadas, setRecusadas] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<string | null>(null);
  const [aplicadas, setAplicadas] = useState<number | null>(null);

  const pedir = useMutation({
    mutationFn: () => api.suggestCategories(),
    onSuccess: (dados) => {
      setResultado(dados);
      setRecusadas(new Set());
      setAplicadas(null);
    },
    onError: (falha) =>
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui falar com a IA.'),
  });

  const aceitas = resultado?.suggestions.filter((s) => !recusadas.has(s.transactionId)) ?? [];

  const aplicar = useMutation({
    mutationFn: () =>
      api.applySuggestions(
        aceitas.map((s) => ({
          transactionId: s.transactionId,
          categoryId: s.categoryId,
          // O motivo que a tela mostrou vira legenda no lancamento: daqui a
          // um mes, "IA: assinatura mensal de streaming" explica sozinho por
          // que aquela linha esta onde esta.
          reason: s.reason,
        })),
      ),
    onSuccess: ({ applied }) => {
      setAplicadas(applied);
      setResultado(null);
      void cliente.invalidateQueries({ queryKey: ['transactions'] });
      void cliente.invalidateQueries({ queryKey: ['dashboard'] });
      void cliente.invalidateQueries({ queryKey: ['payees'] });
      void cliente.invalidateQueries({ queryKey: ['insights'] });
    },
    onError: (falha) =>
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui aplicar.'),
  });

  const alternar = (id: string) => {
    setRecusadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  };

  return (
    <section className="card">
      <div className="between">
        <h2 className="card-title">Sugerir categorias</h2>
        <button className="btn ghost" onClick={() => pedir.mutate()} disabled={pedir.isPending}>
          {pedir.isPending ? 'Pensando...' : 'Pedir sugestao'}
        </button>
      </div>
      <p className="card-hint">
        A IA ja classifica sozinha o que chega do banco. Aqui ela faz outra passada, agora tambem
        sobre o que esta parado em <strong>Outros</strong> — e nada e gravado sem voce confirmar.
      </p>

      {erro && <p className="expense">{erro}</p>}

      {aplicadas !== null && (
        <p className="income">
          {aplicadas === 1
            ? '1 lancamento categorizado.'
            : `${aplicadas} lancamentos categorizados.`}
        </p>
      )}

      {resultado && !resultado.available && <p className="muted">{resultado.unavailableReason}</p>}

      {resultado?.available && resultado.suggestions.length === 0 && (
        <p className="muted">
          {resultado.analyzed === 0
            ? 'Nada em Outros nem sem categoria. Nao ha o que sugerir.'
            : `Olhei ${resultado.analyzed} lancamentos e nao consegui decidir nenhum com seguranca. Ficam para voce.`}
        </p>
      )}

      {resultado?.available && resultado.suggestions.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Usar</th>
                  <th>Lancamento</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th>Categoria sugerida</th>
                  <th>Por que</th>
                </tr>
              </thead>
              <tbody>
                {resultado.suggestions.map((sugestao) => (
                  <tr key={sugestao.transactionId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!recusadas.has(sugestao.transactionId)}
                        onChange={() => alternar(sugestao.transactionId)}
                        aria-label={`Usar a sugestao para ${sugestao.description}`}
                      />
                    </td>
                    <td>
                      <span className="payee-name">
                        <span>{sugestao.description}</span>
                        {sugestao.counterpartyName && (
                          <span className="payee-meta">{sugestao.counterpartyName}</span>
                        )}
                      </span>
                    </td>
                    <td className="money" style={{ textAlign: 'right' }}>
                      {absMoney(sugestao.amountCents)}
                    </td>
                    <td>
                      <CategoryTag name={sugestao.categoryName} colorToken={sugestao.colorToken} />
                    </td>
                    <td className="muted">{sugestao.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ marginTop: 'var(--space-4)' }}>
            <button
              className="btn"
              onClick={() => aplicar.mutate()}
              disabled={aceitas.length === 0 || aplicar.isPending}
            >
              {aplicar.isPending
                ? 'Aplicando...'
                : `Aplicar ${aceitas.length} de ${resultado.suggestions.length}`}
            </button>
            <button className="btn ghost" onClick={() => setResultado(null)}>
              Descartar
            </button>
          </div>
        </>
      )}
    </section>
  );
}
